import { yupResolver } from "@hookform/resolvers/yup";
import { ChartOfAccountAPI } from "api";
import QuillEditor from "components/common/QuillEditor";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { customSettings } from "utils/customConfig";
import * as yup from "yup";
import Select from "react-select";
import CustomOption from "utils/CustomOption";
import { useChartOfAccountContext } from "context/ChartOfAccountContext";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchChartOfAccounts } from "hooks/useFetchChartOfAccounts";
import JoditEditor from "jodit-react";
import { useSettingContext } from "context/SettingContext";

const AddNewChartOfAccount = ({
  setIsOpenModal,
  isOpenModal,
  selectedNode,
  setSelectedNode,
}) => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [data, setData] = useState([]);
  const [description, setDescription] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [accountLevelLocal, setAccountLevelLocal] = useState(0);

  const schema = yup
    .object()
    .shape({
      title: yup.string(),
      classification: yup.string(),
      accountType: yup.string(),
      subClass: yup.string(),
      code: yup.string(),
      parentAccountId: yup.string(),
      accountLevel: yup.string(),
      costCenterId: yup.string(),
      // status: yup.boolean(),
      description: yup.string(),
    })
    .required();

  const submittedValues = {};
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });
  const {
    handleSubmit,
    reset,
    setValue,
    register,
    watch,
    clearErrors,
    formState: { errors },
  } = methods;

  const watchClassification = watch("classification");
  // const {
  //   costCenterId,
  //   costCenterName,
  //   setCostCenterId,
  //   costCenters,
  //   isLoading,
  // } = useChartOfAccountContext();

  const settingsData = useSettingContext();
  const costCenter = useMemo(
    () => settingsData?.find((d) => d.name === "DEFAULT_COST_CENTER"),
    [settingsData]
  );
  const costCenterId = Number(costCenter?.value); // Ensure it's a number
  const { data: chartOfAccountsData } = useFetchChartOfAccounts(costCenterId);

  const onSubmit = async (formData) => {
    const myData = {
      ...formData,
      id: formData.id ? formData.id : 0,
      costCenterId: costCenterId,
      accountLevel: accountLevelLocal ?? 0,
      parentAccountId: selectedOption?.value ?? 0,
    };
    // console.log(myData);
    try {
      if (selectedNode) {
        await ChartOfAccountAPI.post(myData); // Assuming you're updating the category
        toast.success("Chart Of Account Updated Successfully", {
          position: customSettings.toastPosition,
          autoClose: customSettings.toastClose,
        });
      } else {
        await ChartOfAccountAPI.post(myData);
        toast.success("Chart Of Account Created Successfully", {
          position: customSettings.toastPosition,
          autoClose: customSettings.toastClose,
        });
      }

      // ------- Get all object keys from data and set empty values to reset ------------
      const keys = Object.keys(data);
      for (const key of keys) {
        submittedValues[key] = "";
      }
      reset({ ...submittedValues });
      queryClient.invalidateQueries(["chart-of-accounts"]); // Invalidate and refetch
      handleClose();
    } catch (error) {
      toast.error("Something Went Wrong", {
        position: customSettings.toastPosition,
        autoClose: customSettings.toastClose,
      });
      console.log(error);
    }
  };

  useEffect(() => {
    if (data.length > 0) {
      const flatOptions = flattenOptions(data);
      setOptions(flatOptions);
    }
  }, [data]);

  // useEffect(() => {
  //   setValue("description", description);
  // }, [description, setValue]);

  // useEffect(() => {
  //   // Register fields with react-hook-form
  //   register("description");
  // }, [register]);

  useEffect(() => {
    if (selectedNode) {
      // setDescription(selectedNode?.description);
      setValue("description", selectedNode.description || "");
    }
  }, [selectedNode, setValue]);

  useEffect(() => {
    if (chartOfAccountsData) {
      const hierarchicalData = createHierarchy(chartOfAccountsData?.data?.data);
      // console.log("hierarchicalData", hierarchicalData);
      setData(hierarchicalData);
    }
  }, [chartOfAccountsData]);

  useEffect(() => {
    if (data.length > 0) {
      const flatOptions = flattenOptions(data);
      setOptions(flatOptions);
    }
  }, [data]);

  const getNodeDepth = (nodeId, nodes, currentDepth = 1) => {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return currentDepth;
      }
      if (node.children && node.children.length > 0) {
        const depth = getNodeDepth(nodeId, node.children, currentDepth + 1);
        console.log(depth);
        if (depth) {
          return depth;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (selectedOption) {
      setValue("parentAccountId", selectedOption.value);
      const selectedNodeDepth = getNodeDepth(selectedOption.value, data);

      if (selectedNodeDepth) {
        // Set cLevel to the depth of the selected node + 1
        setValue("accountLevel", selectedNodeDepth + 1);
        setAccountLevelLocal(selectedNodeDepth + 1);
      } else {
        // Default to cLevel 1 if no node is found
        setAccountLevelLocal(0);
      }
    } else {
      setAccountLevelLocal(0); // Default to cLevel 1 if no node is selected
    }
  }, [selectedOption, data]);

  const handleClose = () => {
    reset();
    setIsOpenModal(false);
    setSelectedNode(null);
    setSelectedOption(null);
    setDescription(null);
  };

  useEffect(() => {
    if (selectedNode) {
      reset(selectedNode);
      if (selectedNode.parentAccountId) {
        const parentOption = options.find(
          (option) => option.value === selectedNode.parentAccountId
        );
        setSelectedOption(parentOption || null);
      } else {
        setSelectedOption(null);
      }

      // Set cLevel based on the level of the modalContent
      const contentNodeDepth = getNodeDepth(selectedNode.parentAccountId, data);
      if (contentNodeDepth) {
        setAccountLevelLocal(contentNodeDepth + 1);
      } else {
        setAccountLevelLocal(1); // Default level if no parent is found
      }
    } else {
      // Reset form if no modalContent is provided
      reset();
      setSelectedOption(null);
      setAccountLevelLocal(1);
    }
  }, [selectedNode, setValue, options, data, reset]);

  useEffect(() => {
    if (!isOpenModal) {
      reset({
        title: "",
        accountLevel: "",
        accountType: "",
        classification: "",
        code: "",
        costCenterId: "",
        description: "",
        parentAccountId: "",
        subClass: "",
      });
    }
  }, [isOpenModal, reset]);

  return (
    <Modal
      size="lg"
      show={isOpenModal}
      onHide={handleClose}
      contentClassName="border"
    >
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header
            closeButton
            className="bg-body-tertiary border-bottom-0"
          >
            <Modal.Title as="h5">Chart of Account </Modal.Title>
          </Modal.Header>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Button variant="falcon-default" size="sm" className="fs-10">
                Loading...
              </Button>
            </div>
          ) : (
            <Modal.Body className="px-3 py-1">
              <Row className="gx-2 gy-2">
                {/* <Col md="6">
                  <Form.Group>
                    <Form.Select
                      aria-label="type"
                      type="text"
                      title="Cost Center"
                      className="py-2"
                      isInvalid={!!errors?.costCenterId}
                      {...register("costCenterId")}
                    >
                      <option value="">Select Cost Center</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors?.costCenterId?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col> */}
                <Col md="6">
                  <Form.Group>
                    <Form.Select
                      aria-label="classification"
                      type="text"
                      title="Classification"
                      className="py-2"
                      isInvalid={!!errors?.classification}
                      {...register("classification")}
                    >
                      <option value="">Select Classification</option>
                      <option value="Asset">Asset</option>
                      <option value="Equity">Equity</option>
                      <option value="Expense">Expense</option>
                      <option value="Income">Income</option>
                      <option value="Liability">Liability</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors?.classification?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md="6">
                  <Form.Group>
                    <Form.Select
                      aria-label="subClass"
                      type="text"
                      title="Sub Class"
                      className="py-2"
                      isInvalid={!!errors?.subClass}
                      {...register("subClass")}
                      disabled={!watchClassification}
                    >
                      <option value="">Select Sub Class</option>
                      {watchClassification === "Asset" && (
                        <>
                          <option value="Bank">Bank</option>
                          <option value="Cash">Cash</option>
                          <option value="Fixed Assets">Fixed Assets</option>
                          <option value="Inventory">Inventory</option>
                          <option value="Receivable">Receivable</option>
                          <option value="Other">Other</option>
                        </>
                      )}
                      {watchClassification === "Expense" && (
                        <>
                          <option value="Cost of Goods Sold">
                            Cost of Goods Sold
                          </option>
                          <option value="Depreciation">Depreciation</option>
                          <option value="Wages">Wages</option>
                          <option value="Other">Other</option>
                        </>
                      )}
                      {watchClassification === "Equity" && (
                        <>
                          <option value="Equity">Equity</option>
                          <option value="Other">Other</option>
                        </>
                      )}
                      {watchClassification === "Income" && (
                        <>
                          <option value="Income Account">Income Account</option>
                          <option value="Other">Other</option>
                        </>
                      )}
                      {watchClassification === "Liability" && (
                        <>
                          <option value="Accounts Payable">
                            Accounts Payable
                          </option>
                          <option value="Tax">Tax</option>
                          <option value="Other">Other</option>
                        </>
                      )}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors?.subClass?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md="6">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Code"
                      title="Code"
                      className="py-2"
                      isInvalid={!!errors.code}
                      {...register("code")}
                    />
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Control.Feedback
                        type="invalid"
                        className="d-inline w-75"
                      >
                        {errors.code?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>

                <Col md="6">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Title"
                      title="Title"
                      className="py-2"
                      isInvalid={!!errors.title}
                      {...register("title")}
                    />
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Control.Feedback
                        type="invalid"
                        className="d-inline w-75"
                      >
                        {errors.title?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>

                <Col md="6">
                  <Form.Group>
                    <Form.Select
                      aria-label="type"
                      type="text"
                      title="Type"
                      className="py-2"
                      isInvalid={!!errors?.accountType}
                      {...register("accountType")}
                    >
                      <option value="">Select Type</option>
                      <option value="Group">Group</option>
                      <option value="Ledger">Ledger</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors?.accountType?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md="6">
                  <Form.Group controlId="parent">
                    {/* <Form.Label className="fs-10">Parent</Form.Label> */}
                    <div title="Parent Account">
                      <Select
                        options={options}
                        components={{ Option: CustomOption }}
                        isClearable
                        isDisabled={!!selectedNode}
                        value={selectedOption}
                        onChange={setSelectedOption}
                        placeholder="Parent Account"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        styles={{
                          option: (provided, state) => ({
                            ...provided,
                            paddingLeft: `${state.data.accountLevel * 20}px`,
                          }),
                          control: (provided) => ({
                            ...provided,
                            border: "1px solid #dbdbdb",
                            padding: "0.1rem 0.1rem",
                          }),
                          menu: (provided) => ({
                            ...provided,
                            height: 170,
                            color: "black",
                            zIndex: 1, // High enough to ensure visibility
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            padding: 0,
                            height: 170,
                            zIndex: 1, // High enough to ensure visibility
                          }),
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-1 mt-1" controlId="url">
                {/* <QuillEditor
                  setValue={setDescription}
                  value={description}
                  placeholder={"Description"}
                /> */}

                <JoditEditor
                  config={{
                    showCharsCounter: false,
                    showWordsCounter: false,
                    showXPathInStatusbar: false, // ✅ Removes footer bar
                    buttons: [
                      "source",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "superscript",
                      "subscript",
                      "ul",
                      "ol",
                      "indent",
                      "font",
                      "fontsize",
                      "brush",
                      "paragraph",
                      "align",
                      "hr",
                      "eraser",
                      "copyformat",
                      "symbol",
                      "fullsize",
                      "print",
                      "about",
                      "selectall",
                      // "copy",
                      "paste",
                      "link",
                      "unlink",
                      "image",
                      "file",
                      "video",
                      "table",
                      "preview",
                      "dots", // Dots button expands extra options
                    ],
                    placeholder: "Description",
                    toolbarAdaptive: false, // Show full toolbar without collapsing
                    toolbarSticky: false, // Keep toolbar fixed when scrolling
                    uploader: {
                      insertImageAsBase64URI: true, // ✅ Allows adding Base64 images directly
                    },
                  }}
                  value={watch("description")} // ✅ Watch ensures form binding
                  onBlur={(value) => setValue("description", value)} // preferred to use only this option to update the content
                />
              </Form.Group>
            </Modal.Body>
          )}

          <Modal.Footer className="bg-body-tertiary px-x1 border-top-0">
            <div onClick={handleClose} className="px-4 mx-0 cursor-pointer">
              Cancel
            </div>
            <Button variant="primary" type="submit" className="px-4 mx-0">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  );
};

const flattenOptions = (nodes, accountLevel = 1) => {
  return nodes.reduce((acc, node) => {
    const label = `${"    ".repeat(accountLevel)}${node.title}`; // Add 4 spaces per accountLevel
    acc.push({
      value: node.id,
      label,
      parentAccountId: node.parentAccountId,
      accountLevel,
    });

    if (node.children && node.children.length > 0) {
      acc.push(...flattenOptions(node.children, accountLevel + 1));
    }

    return acc;
  }, []);
};

const createHierarchy = (data) => {
  const lookup = {};
  const result = [];

  data.forEach((item) => {
    lookup[item.id] = { ...item, children: [] };
  });

  data.forEach((item) => {
    const { id, parentAccountId } = item;
    if (parentAccountId === 0) {
      result.push(lookup[id]);
    } else if (lookup[parentAccountId]) {
      lookup[parentAccountId].children.push(lookup[id]);
    }
  });

  return result;
};

export default AddNewChartOfAccount;
